import { useEffect, useState } from "react";
import { Save, Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ImageField } from "@/components/admin/MediaPicker";

const inputCls = "w-full bg-ink-800 border border-ink-600 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand transition-colors text-white";
const labelCls = "text-xs text-slate-400 font-mono-label";

export default function Settings() {
  const { reload } = useSettings();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS({ hero: {}, social: {}, nav: [], ...r.data })).catch(() => {}); }, []);
  if (!s) return <div className="text-brand font-mono-label">loading settings...</div>;

  const set = (patch) => setS((p) => ({ ...p, ...patch }));
  const setHero = (patch) => setS((p) => ({ ...p, hero: { ...p.hero, ...patch } }));
  const setSocial = (patch) => setS((p) => ({ ...p, social: { ...p.social, ...patch } }));
  const setNav = (nav) => setS((p) => ({ ...p, nav }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/settings", { data: s }); toast.success("Settings saved"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const Section = ({ title, children }) => (
    <div className="rounded-xl border border-ink-600 bg-ink-900 p-6 space-y-4"><div className="label-mono">{title}</div>{children}</div>
  );

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div><div className="label-mono mb-1">Configuration</div><h1 className="font-display font-extrabold text-white text-3xl">Site Settings</h1></div>
        <button onClick={save} disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 bg-brand text-brand-dark font-bold text-sm px-5 py-2.5 rounded-full hover:bg-brand-neon transition-colors disabled:opacity-60"><Save size={16} /> {saving ? "Saving..." : "Save changes"}</button>
      </div>

      <div className="space-y-6">
        <Section title="Brand">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Site name</label><input value={s.site_name || ""} onChange={(e) => set({ site_name: e.target.value })} data-testid="setting-site-name" className={`${inputCls} mt-1.5`} /></div>
            <div><label className={labelCls}>Tagline</label><input value={s.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
          </div>
          <div><label className={labelCls}>Footer text</label><textarea value={s.footer_text || ""} onChange={(e) => set({ footer_text: e.target.value })} rows={2} className={`${inputCls} mt-1.5`} /></div>
          <ImageField value={s.logo} onChange={(url) => set({ logo: url })} label="Logo (optional)" />
        </Section>

        <Section title="Homepage Hero">
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className={labelCls}>Headline start</label><input value={s.hero.headline_prefix || ""} onChange={(e) => setHero({ headline_prefix: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
            <div><label className={labelCls}>Highlighted word</label><input value={s.hero.headline_highlight || ""} onChange={(e) => setHero({ headline_highlight: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
            <div><label className={labelCls}>Headline end</label><input value={s.hero.headline_suffix || ""} onChange={(e) => setHero({ headline_suffix: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
          </div>
          <div><label className={labelCls}>Subtitle</label><input value={s.hero.subtitle || ""} onChange={(e) => setHero({ subtitle: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>CTA text</label><input value={s.hero.cta_text || ""} onChange={(e) => setHero({ cta_text: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
            <div><label className={labelCls}>CTA link</label><input value={s.hero.cta_link || ""} onChange={(e) => setHero({ cta_link: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
          </div>
          <ImageField value={s.hero.image} onChange={(url) => setHero({ image: url })} label="Hero image" />
        </Section>

        <Section title="Navigation">
          <div className="space-y-2">
            {(s.nav || []).map((n, i) => (
              <div key={i} className="flex items-center gap-2">
                <GripVertical size={15} className="text-ink-600" />
                <input value={n.label} onChange={(e) => { const nav = [...s.nav]; nav[i] = { ...nav[i], label: e.target.value }; setNav(nav); }} className={`${inputCls} flex-1`} placeholder="Label" />
                <input value={n.link} onChange={(e) => { const nav = [...s.nav]; nav[i] = { ...nav[i], link: e.target.value }; setNav(nav); }} className={`${inputCls} flex-1`} placeholder="/link" />
                <button onClick={() => setNav(s.nav.filter((_, x) => x !== i))} className="p-2 text-slate-500 hover:text-red-400"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <button onClick={() => setNav([...(s.nav || []), { label: "NEW", link: "/" }])} className="inline-flex items-center gap-1.5 text-sm text-brand font-semibold"><Plus size={14} /> Add nav item</button>
          <div><label className={labelCls}>Start Here link</label><input value={s.start_here_link || ""} onChange={(e) => set({ start_here_link: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
        </Section>

        <Section title="Social Links">
          <div className="grid sm:grid-cols-2 gap-4">
            {["instagram", "twitter", "youtube", "linkedin"].map((k) => (
              <div key={k}><label className={`${labelCls} capitalize`}>{k}</label><input value={s.social[k] || ""} onChange={(e) => setSocial({ [k]: e.target.value })} className={`${inputCls} mt-1.5`} placeholder={`https://${k}.com/...`} /></div>
            ))}
          </div>
        </Section>

        <Section title="Newsletter">
          <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><input type="checkbox" checked={s.newsletter_enabled !== false} onChange={(e) => set({ newsletter_enabled: e.target.checked })} className="accent-emerald-500" /> Show newsletter signup areas</label>
          <div><label className={labelCls}>Newsletter headline</label><input value={s.newsletter_headline || ""} onChange={(e) => set({ newsletter_headline: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
          <div><label className={labelCls}>Newsletter subtext</label><input value={s.newsletter_subtext || ""} onChange={(e) => set({ newsletter_subtext: e.target.value })} className={`${inputCls} mt-1.5`} /></div>
        </Section>

        <Section title="SEO Defaults">
          <div><label className={labelCls}>Default meta description</label><textarea value={s.default_seo_description || ""} onChange={(e) => set({ default_seo_description: e.target.value })} rows={2} className={`${inputCls} mt-1.5`} /></div>
          <ImageField value={s.default_social_image} onChange={(url) => set({ default_social_image: url })} label="Default social share image" />
        </Section>
      </div>
    </div>
  );
}
